import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, {
  coreErrorMessage,
  formatMinor,
  moneyAmount,
} from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = {
  districts: [],
  selected: 0,
  loaded: false,
  loading: false,
  error: null,
  notice: null,
  ticketError: null,
  v2Off: false,
};

let loadPromise = null;

export function _resetTicketsState(overrides = {}) {
  Object.assign(
    state,
    {
      districts: [],
      selected: 0,
      loaded: false,
      loading: false,
      error: null,
      notice: null,
      ticketError: null,
      v2Off: false,
    },
    overrides,
  );
  loadPromise = null;
}

export function mapDistricts(content) {
  return (content?.districts || [])
    .filter((district) => district.status === 'open')
    .map((district) => ({
      id: district.districtId,
      label: String(district.name || district.districtId).toUpperCase(),
      riskTier: String(district.riskTier || '--').toUpperCase(),
    }));
}

export function ticketErrorMessage(error) {
  if (!error) return 'TICKET LINE DOWN';
  if (error.code === 'CORE-409-004') return 'TICKET ALREADY ISSUED TODAY';
  if (error.code === 'CORE-409-005') return 'TOMORROW IS ALREADY SET';
  if (error.code === 'CORE-400-024') return 'NO TICKETS ARE SOLD ON PURGE DAY';
  if (error.code === 'CORE-400-003') return 'INSUFFICIENT CASH FOR THE TARIFF';
  if (error.code === 'CORE-400-005') return 'ALREADY IN THAT DISTRICT';
  if (error.code === 'CORE-404-012') return 'TICKET LINE CLOSED // V2 OFFLINE';
  return coreErrorMessage(error);
}

function defaultSelection(districts, currentDistrictId) {
  const index = districts.findIndex((district) => district.id !== currentDistrictId);
  return index >= 0 ? index : 0;
}

export async function loadTicketsData() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    state.loading = true;
    try {
      const session = useCoreSession.getState();
      const content = await session.loadContent();
      const districts = mapDistricts(content);
      state.districts = districts;
      state.selected = defaultSelection(districts, useCoreSession.getState().state?.districtId);
      state.loaded = true;
    } catch (error) {
      state.error = coreErrorMessage(error);
      state.loaded = true;
    }

    try {
      await useCoreSession.getState().loadTicket();
      state.ticketError = null;
      state.v2Off = false;
    } catch (error) {
      state.ticketError = ticketErrorMessage(error);
      state.v2Off = error?.code === 'CORE-404-012';
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

function selectedDistrict() {
  return state.districts[state.selected] || state.districts[0] || null;
}

function districtById(id) {
  return state.districts.find((district) => district.id === id) || null;
}

function ticketIdentity(ticket) {
  if (!ticket) return null;
  return `${ticket.seasonId || '?'}:${ticket.dayIndex ?? '?'}:${ticket.toDistrictId || '?'}`;
}

export function tomorrowTicket(session) {
  return session?.tomorrow?.ticket ?? session?.ticket ?? null;
}

export function isTomorrowSet(session) {
  if (session?.tomorrow) return Boolean(session.tomorrow.set || session.tomorrow.ticket);
  return Boolean(session?.ticket);
}

export function tariffMinor(session) {
  const minor = Number(session?.tomorrow?.tariff?.amountMinor);
  return Number.isFinite(minor) ? minor : null;
}

function tariffLabel(session) {
  const minor = tariffMinor(session);
  return minor == null ? null : formatMinor({ amountMinor: minor });
}

function hasCashForTariff(session) {
  const minor = tariffMinor(session);
  if (minor == null) return true;
  return Math.round(moneyAmount(session?.state?.cash) * 100) >= minor;
}

export function canLayLow(session) {
  const tomorrow = session?.tomorrow;
  return Boolean(tomorrow?.canSet && !tomorrow.set && !tomorrow.layLow);
}

export function canBuyTicket(session) {
  const canSet = session?.tomorrow ? Boolean(session.tomorrow.canSet) : true;
  return Boolean(
    session?.status === 'online' &&
      session.token &&
      !state.loading &&
      !state.v2Off &&
      canSet &&
      !isTomorrowSet(session) &&
      selectedDistrict() &&
      hasCashForTariff(session),
  );
}

export function pastTicketHistory(session) {
  const current = ticketIdentity(tomorrowTicket(session));
  const rows = Array.isArray(session?.ticketHistory) ? [...session.ticketHistory] : [];
  return rows
    .filter((row) => ticketIdentity(row) !== current)
    .sort((a, b) => {
      const day = (Number(b?.dayIndex) || 0) - (Number(a?.dayIndex) || 0);
      if (day !== 0) return day;
      return String(b?.createdAt || '').localeCompare(String(a?.createdAt || ''));
    });
}

function ticketDestination(ticket) {
  const district = districtById(ticket?.toDistrictId);
  return {
    label: district?.label || String(ticket?.toDistrictId || '--').toUpperCase(),
    riskTier: district?.riskTier || '--',
  };
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

async function buySelected() {
  const district = selectedDistrict();
  if (!district || !canBuyTicket(useCoreSession.getState())) return;
  state.loading = true;
  state.error = null;
  state.notice = null;
  try {
    await useCoreSession.getState().buyTicket(district.id);
    state.notice = `TICKET ISSUED // ${district.label}`;
    state.ticketError = null;
    state.v2Off = false;
  } catch (error) {
    state.error = ticketErrorMessage(error);
    const issued = error?.payload?.details?.ticket;
    if (issued) {
      useCoreSession.setState({
        ticket: issued,
        tomorrow: {
          ...(useCoreSession.getState().tomorrow || {}),
          set: true,
          layLow: false,
          ticket: issued,
        },
      });
    }
  } finally {
    state.loading = false;
  }
}

async function layLowSelected() {
  const session = useCoreSession.getState();
  if (!canLayLow(session) || state.loading) return;
  state.loading = true;
  state.error = null;
  state.notice = null;
  try {
    const result = await useCoreSession.getState().acknowledgeLayLow();
    const forDay = result?.forDay ?? useCoreSession.getState().tomorrow?.forDay;
    state.notice = `LAY LOW SET // DAY ${forDay ?? '--'}`;
    state.ticketError = null;
    state.v2Off = false;
  } catch (error) {
    state.error = ticketErrorMessage(error);
    if (error?.code === 'CORE-409-005') {
      try {
        await useCoreSession.getState().loadTicket();
      } catch {
        /* keep the conflict copy visible */
      }
    }
  } finally {
    state.loading = false;
  }
}

function layoutFor(safe) {
  const mobile = safe.width < 500;
  if (!mobile) {
    return {
      mobile,
      destinations: { x: 8, y: 34, w: 330, h: 246 },
      tomorrow: { x: 346, y: 34, w: safe.width - 354, h: 150 },
      history: { x: 346, y: 190, w: safe.width - 354, h: 90 },
    };
  }

  const x = 8;
  const w = safe.width - 16;
  const compact = safe.height < 560;
  const room = Math.max(180, safe.height - 190);
  const destinationsH = compact
    ? Math.max(90, Math.round(room * 0.55))
    : Math.max(170, Math.round(safe.height * 0.28));
  const tomorrowH = compact
    ? Math.max(70, Math.round(room * 0.35))
    : Math.max(120, Math.round(safe.height * 0.2));
  const historyH = compact ? 0 : Math.max(70, Math.round(safe.height * 0.13));

  const destinations = { x, y: 32, w, h: destinationsH };
  const tomorrow = { x, y: destinations.y + destinationsH + 6, w, h: tomorrowH };
  const history = historyH >= 50
    ? { x, y: tomorrow.y + tomorrowH + 6, w, h: historyH }
    : null;
  return { mobile, destinations, tomorrow, history };
}

function drawTop(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // TICKETS', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'TOMORROW // RECEIPTS', 216, 4, TERMINAL.TEXT_DIM);
  text(ctx, online ? 'TICKET LINE' : 'OFFLINE', safe.width - 88, 4, online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawDistricts(ctx, box, mobile, session) {
  const { x, y, w, h } = box;
  DopeUI.Panel(ctx, x, y, w, h, 'DESTINATIONS // RISK TIER');
  const tariff = tariffLabel(session);
  if (mobile) {
    text(ctx, tariff ? `#  DISTRICT // ${tariff}` : '#  DISTRICT', x + 10, y + 24, TERMINAL.TEXT_DIM);
  } else {
    const header = tariff
      ? `#  DISTRICT            RISK    ${tariff}`
      : '#  DISTRICT            RISK';
    text(ctx, header, x + 10, y + 24, TERMINAL.TEXT_DIM);
  }
  rule(ctx, x + 8, y + 40, w - 16);

  const rowH = mobile ? 44 : 30;
  const top = y + 48;
  state.districts.forEach((district, index) => {
    const rowY = top + index * rowH;
    if (rowY + rowH > y + h - 6) return;
    const selected = index === state.selected;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, rowH - 4);
    }
    text(ctx, selected ? '>' : ' ', x + 10, rowY, TERMINAL.TEXT_BRIGHT);
    if (mobile) {
      text(ctx, `${index + 1} ${district.label.slice(0, 24)}`, x + 22, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
      const detail = tariff
        ? `RISK ${district.riskTier} // TARIFF ${tariff}`
        : `RISK ${district.riskTier}`;
      text(ctx, detail, x + 22, rowY + 16, TERMINAL.TEXT_DIM);
    } else {
      const risk = district.riskTier.padEnd(7).slice(0, 7);
      const line = `${index + 1}  ${district.label.padEnd(18).slice(0, 18)} ${risk}${tariff ? ` ${tariff}` : ''}`;
      text(ctx, line, x + 22, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    }
    interactiveRegistry.register({
      id: `tickets-district-${district.id}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: rowH,
      onClick: () => {
        state.selected = index;
        state.error = null;
        state.notice = null;
      },
    });
  });

  if (!state.districts.length) {
    text(ctx, state.loaded ? 'NO OPEN DISTRICTS' : 'READING THE LINE...', x + 10, top, TERMINAL.TEXT_DIM);
  }
}

function drawTomorrow(ctx, box, session, mobile) {
  const { x, y, w, h } = box;
  const tomorrow = session.tomorrow;
  const ticket = tomorrowTicket(session);
  const set = isTomorrowSet(session);
  const title = set
    ? 'TICKET ON FILE'
    : tomorrow?.layLow
      ? 'LAY LOW'
      : tomorrow && !tomorrow.canSet
        ? 'PURGE DAY'
        : 'NO TICKET ON FILE';
  DopeUI.Panel(ctx, x, y, w, h, title);

  const line = mobile ? 18 : 20;
  let row = y + 28;
  const maxRow = y + h - 22;

  if (set && ticket) {
    const destination = ticketDestination(ticket);
    const tariff = ticket.tariffPaid ? formatMinor(ticket.tariffPaid) : tariffLabel(session);
    const valid = tomorrow?.forDay ?? (Number(ticket.dayIndex) + 1);
    const rows = [
      ['DEST', destination.label.slice(0, mobile ? 20 : 18), TERMINAL.TEXT_BRIGHT],
      ['RISK', destination.riskTier, TERMINAL.TEXT],
      ['TARIFF', tariff || '--', TERMINAL.TEXT],
      ['ISSUED', `DAY ${ticket.dayIndex}`, TERMINAL.TEXT],
      ['VALID', `DAY ${valid}`, TERMINAL.TEXT],
    ];
    rows.forEach(([label, value, color]) => {
      if (row > maxRow) return;
      text(ctx, label, x + 10, row, TERMINAL.TEXT_DIM);
      text(ctx, value, x + 62, row, color);
      row += line;
    });
  } else if (tomorrow?.layLow) {
    const rows = [
      'LAY LOW - FREE',
      'NO TICKET. NO CHARGE.',
      'STAY PUT; POSITIONS PERSIST.',
      tomorrow?.forDay ? `SET FOR DAY ${tomorrow.forDay}` : 'SET FOR TOMORROW',
    ];
    rows.forEach((value, index) => {
      if (row > maxRow) return;
      text(ctx, value, x + 10, row, index === 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
      row += line;
    });
  } else if (tomorrow && !tomorrow.canSet) {
    ['NO TICKET SOLD.', 'NO PLAY SET FOR TOMORROW.'].forEach((value, index) => {
      if (row > maxRow) return;
      text(ctx, value, x + 10, row, index === 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
      row += line;
    });
  } else {
    const tariff = tariffLabel(session);
    const rows = [
      ['NO TICKET = LAY LOW.', TERMINAL.TEXT],
      ['SET A DESTINATION OR LAY LOW.', TERMINAL.TEXT_DIM],
      ['LAY LOW - FREE', TERMINAL.TEXT],
      [tariff ? `TARIFF FIXED AT ${tariff}.` : 'TARIFF FIXED. NO HAGGLING.', TERMINAL.TEXT_DIM],
    ];
    rows.forEach(([value, color]) => {
      if (row > maxRow) return;
      text(ctx, value, x + 10, row, color);
      row += line;
    });
  }

  const note = state.notice || state.error || state.ticketError;
  if (note) {
    const color = state.error || state.ticketError ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN;
    const max = Math.max(8, Math.floor((w - 20) / 8));
    text(ctx, `// ${note}`.slice(0, max), x + 10, y + h - 16, color);
  }
}

function drawHistory(ctx, box, session, mobile) {
  const { x, y, w, h } = box;
  DopeUI.Panel(ctx, x, y, w, h, 'RECENT RECEIPTS');
  const rows = pastTicketHistory(session);
  if (!rows.length) {
    text(ctx, 'NO PAST TICKETS.', x + 10, y + 26, TERMINAL.TEXT_DIM);
    return;
  }
  const rowH = 16;
  const maxRows = Math.max(1, Math.floor((h - 28) / rowH));
  const max = Math.max(8, Math.floor((w - 20) / 8));
  rows.slice(0, maxRows).forEach((ticket, index) => {
    const destination = ticketDestination(ticket);
    const tariff = ticket.tariffPaid ? formatMinor(ticket.tariffPaid) : null;
    const line = mobile
      ? `DAY ${ticket.dayIndex} -> ${destination.label}${tariff ? ` ${tariff}` : ''}`
      : `DAY ${ticket.dayIndex} -> ${destination.label.padEnd(16).slice(0, 16)} ${tariff || ''}`;
    text(ctx, line.slice(0, max), x + 10, y + 26 + index * rowH, TERMINAL.TEXT_DIM);
  });
}

function drawFooter(ctx, safe, session, mobile) {
  const set = isTomorrowSet(session);
  const buyEnabled = canBuyTicket(session);
  const layEnabled = canLayLow(session) && !state.loading;
  const buyLabel = state.loading
    ? 'WORKING...'
    : set
      ? 'TICKET SET'
      : 'BUY TICKET';
  const layLabel = session.tomorrow?.layLow ? 'LAY LOW SET' : 'LAY LOW - FREE';

  if (mobile) {
    const backY = safe.height - 52;
    const backW = safe.width - 16;
    if (set) {
      const buyY = backY - 52;
      DopeUI.Button(ctx, 8, buyY, backW, 44, buyLabel, 'disabled');
      DopeUI.Button(ctx, 8, backY, backW, 44, 'BACK TO DESKTOP', 'normal');
      interactiveRegistry.register({
        id: 'tickets-back',
        x: 8,
        y: backY,
        w: backW,
        h: 44,
        onClick: () => navigateTo('main_menu'),
      });
      return;
    }
    const layY = backY - 52;
    const buyY = layY - 52;
    DopeUI.Button(ctx, 8, buyY, backW, 44, buyLabel, buyEnabled ? 'hover' : 'disabled');
    if (buyEnabled) {
      interactiveRegistry.register({
        id: 'tickets-buy',
        x: 8,
        y: buyY,
        w: backW,
        h: 44,
        onClick: buySelected,
      });
    }
    DopeUI.Button(ctx, 8, layY, backW, 44, layLabel, layEnabled ? 'normal' : 'disabled');
    if (layEnabled) {
      interactiveRegistry.register({
        id: 'tickets-lay-low',
        x: 8,
        y: layY,
        w: backW,
        h: 44,
        onClick: layLowSelected,
      });
    }
    DopeUI.Button(ctx, 8, backY, backW, 44, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'tickets-back',
      x: 8,
      y: backY,
      w: backW,
      h: 44,
      onClick: () => navigateTo('main_menu'),
    });
    return;
  }

  rule(ctx, 0, 302, safe.width);
  text(ctx, 'ONE TICKET. ONE DAY.', 8, 312, TERMINAL.TEXT_DIM);
  const y = 344;
  const h = 28;
  if (set) {
    DopeUI.Button(ctx, 8, y, 190, h, buyLabel, 'disabled');
    DopeUI.Button(ctx, 206, y, safe.width - 214, h, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'tickets-back',
      x: 206,
      y,
      w: safe.width - 214,
      h,
      onClick: () => navigateTo('main_menu'),
    });
  } else {
    DopeUI.Button(ctx, 8, y, 148, h, buyLabel, buyEnabled ? 'hover' : 'disabled');
    if (buyEnabled) {
      interactiveRegistry.register({
        id: 'tickets-buy',
        x: 8,
        y,
        w: 148,
        h,
        onClick: buySelected,
      });
    }
    DopeUI.Button(ctx, 164, y, 180, h, layLabel, layEnabled ? 'normal' : 'disabled');
    if (layEnabled) {
      interactiveRegistry.register({
        id: 'tickets-lay-low',
        x: 164,
        y,
        w: 180,
        h,
        onClick: layLowSelected,
      });
    }
    DopeUI.Button(ctx, 352, y, safe.width - 360, h, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'tickets-back',
      x: 352,
      y,
      w: safe.width - 360,
      h,
      onClick: () => navigateTo('main_menu'),
    });
  }
  text(ctx, 'DOPE CHAIN WARS // TICKETS', safe.width - 216, 384, TERMINAL.GREEN_DARK);
}

registerView('tickets', function renderTickets(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadTicketsData();
  }

  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);

  drawTop(ctx, safe, session);
  const layout = layoutFor(safe);
  drawDistricts(ctx, layout.destinations, layout.mobile, session);
  drawTomorrow(ctx, layout.tomorrow, session, layout.mobile);
  if (layout.history) drawHistory(ctx, layout.history, session, layout.mobile);
  drawFooter(ctx, safe, session, layout.mobile);
});

export default 'tickets';
